/**
 * CrmDrawingsSection — takeoff drawings management inside the opportunity modal (Phase 1 of the
 * PDF takeoff tool). Lists the opportunity's extracted single-page drawings; "Add drawings"
 * uploads a full client-sent PDF, opens a page picker (pdf.js thumbnails), and extracts the
 * picked pages server-side into individual drawings. Scale calibration and region markup come
 * in later phases; the "Scaled" chip reflects scale_points_per_foot.
 *
 * Data goes through services/takeoffs.js only.
 */
import { useEffect, useRef, useState } from 'react'
import CrmModal, { F } from './CrmModal.jsx'
import { confirmDialog, promptDialog, alertDialog } from '../Dialog.jsx'
import TakeoffEditor from '../takeoff/TakeoffEditor.jsx'
import {
  DRAWING_TYPES, drawingTypeLabel,
  listDrawings, uploadTempPdf, extractPages, discardTempPdf, updateDrawing, deleteDrawing,
} from '../../services/takeoffs.js'

const PDFJS_SRC    = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
const PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'

/** Dynamic CDN load of pdf.js — same pattern/version as SketchPage.jsx. */
async function ensurePdfJs() {
  if (!window.pdfjsLib) {
    await new Promise(resolve => {
      const s = document.createElement('script')
      s.src = PDFJS_SRC
      s.onload = resolve
      s.onerror = resolve
      document.head.appendChild(s)
    })
    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER
    }
  }
  return window.pdfjsLib || null
}

export default function CrmDrawingsSection({ opportunityId }) {
  const [drawings, setDrawings] = useState(null)   // null = loading
  const [picker, setPicker] = useState(null)       // null closed | { phase:'loading'|'pick'|'extracting', fileName, pages }
  const [busyId, setBusyId] = useState(null)       // drawing id with an in-flight rename/delete
  const [editorDrawing, setEditorDrawing] = useState(null)   // drawing open in the takeoff editor
  const loadedRef = useRef(false)
  const fileInputRef = useRef(null)
  const tmpPromiseRef = useRef(null)               // Promise<tmpPath> for the staged full PDF

  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    listDrawings(opportunityId).then(setDrawings).catch(() => setDrawings([]))
  }, [opportunityId])

  function reload() { listDrawings(opportunityId).then(setDrawings).catch(() => {}) }

  /* ── Add drawings: file → temp upload + local thumbnails → picker ────────── */
  async function onFilePicked(e) {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''   // allow re-picking the same file
    if (!file) return
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      await alertDialog({ title: 'PDF only', message: 'Drawings are uploaded as PDF files.' })
      return
    }

    setPicker({ phase: 'loading', fileName: file.name, pages: [] })
    tmpPromiseRef.current = uploadTempPdf(file)
    tmpPromiseRef.current.catch(() => {})   // surfaced at confirm time; avoid unhandled rejection

    try {
      const pdfjs = await ensurePdfJs()
      if (!pdfjs) throw new Error('PDF preview could not load. Check your connection and try again.')

      const buf = await file.arrayBuffer()
      const pdf = await pdfjs.getDocument({ data: buf }).promise
      const baseName = file.name.replace(/\.pdf$/i, '')

      const pages = []
      for (let n = 1; n <= pdf.numPages; n++) {
        const page = await pdf.getPage(n)
        const vp1 = page.getViewport({ scale: 1 })
        const scale = 150 / vp1.width
        const vp = page.getViewport({ scale })
        const canvas = document.createElement('canvas')
        canvas.width = Math.ceil(vp.width)
        canvas.height = Math.ceil(vp.height)
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise
        pages.push({
          page: n,
          thumb: canvas.toDataURL('image/jpeg', 0.75),
          selected: false,
          name: `${baseName} p${n}`,
          drawingType: '',
        })
        // Show progress as thumbnails come in
        setPicker(p => p && p.phase === 'loading' ? { ...p, pages: [...pages] } : p)
      }
      setPicker(p => p ? { ...p, phase: 'pick', pages } : p)
    } catch (err) {
      await cancelPicker()
      await alertDialog({ title: 'Could not read PDF', message: err.message || 'The file could not be opened.' })
    }
  }

  async function cancelPicker() {
    setPicker(null)
    const tmpPromise = tmpPromiseRef.current
    tmpPromiseRef.current = null
    if (tmpPromise) {
      try { await discardTempPdf(await tmpPromise) } catch (_) {}
    }
  }

  async function confirmPicker() {
    if (!picker) return
    const selected = picker.pages.filter(p => p.selected)
    if (!selected.length) return
    setPicker(p => ({ ...p, phase: 'extracting' }))
    try {
      const tmpPath = await tmpPromiseRef.current
      await extractPages({
        opportunityId,
        tmpPath,
        sourcePdfName: picker.fileName,
        pages: selected.map(p => ({
          page: p.page,
          name: p.name.trim() || `${picker.fileName} p${p.page}`,
          drawingType: p.drawingType || undefined,
        })),
      })
      tmpPromiseRef.current = null
      setPicker(null)
      reload()
    } catch (err) {
      setPicker(p => p ? { ...p, phase: 'pick' } : p)
      await alertDialog({ title: 'Upload failed', message: err.message || 'The pages could not be added. Try again.' })
    }
  }

  function setPage(pageNum, patch) {
    setPicker(p => p ? { ...p, pages: p.pages.map(pg => pg.page === pageNum ? { ...pg, ...patch } : pg) } : p)
  }

  /* ── Row actions ─────────────────────────────────────────────────────────── */
  async function renameDrawing(d) {
    const name = await promptDialog({ title: 'Rename drawing', defaultValue: d.name, required: true })
    if (name === null || !name.trim() || name.trim() === d.name) return
    setBusyId(d.id)
    try {
      await updateDrawing(d.id, { name: name.trim() })
      setDrawings(list => (list || []).map(x => x.id === d.id ? { ...x, name: name.trim() } : x))
    } catch (err) {
      await alertDialog({ title: 'Rename failed', message: err.message })
    } finally { setBusyId(null) }
  }

  async function removeDrawing(d) {
    const ok = await confirmDialog({
      title: 'Delete drawing?',
      message: `Delete "${d.name}"? Any takeoff regions drawn on it are deleted with it.`,
      confirmLabel: 'Delete drawing',
      danger: true,
    })
    if (!ok) return
    setBusyId(d.id)
    try {
      await deleteDrawing(d.id)
      setDrawings(list => (list || []).filter(x => x.id !== d.id))
    } catch (err) {
      await alertDialog({ title: 'Delete failed', message: err.message })
    } finally { setBusyId(null) }
  }

  const selectedCount = picker ? picker.pages.filter(p => p.selected).length : 0

  return (
    <>
      <div style={S.sectionRow}>
        <span style={S.sectionLbl}>Drawings</span>
        <div style={{ flex: 1 }} />
        <button style={{ ...F.btnGhost, padding: '4px 10px' }} onClick={() => fileInputRef.current && fileInputRef.current.click()}>+ Add drawings</button>
        <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} onChange={onFilePicked} />
      </div>

      {drawings === null ? <div style={S.muted}>Loading…</div>
        : drawings.length === 0 ? <div style={S.muted}>No drawings yet. Upload a client PDF to start a takeoff.</div>
        : drawings.map(d => (
          <div key={d.id} style={{ ...S.row, opacity: busyId === d.id ? 0.5 : 1 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <button style={S.nameBtn} title="Rename" onClick={() => renameDrawing(d)}>{d.name}</button>
              <div style={S.source}>
                {[d.source_pdf_name, d.source_page ? `page ${d.source_page}` : null].filter(Boolean).join(' · ') || 'Uploaded PDF'}
              </div>
            </div>
            {d.drawing_type && <span style={S.typeChip}>{drawingTypeLabel(d.drawing_type)}</span>}
            <span style={d.scale_points_per_foot ? S.scaledChip : S.unscaledChip}>
              {d.scale_points_per_foot ? 'Scaled' : 'Not scaled'}
            </span>
            <button style={S.openBtn} title="Open in the takeoff editor" onClick={() => setEditorDrawing(d)}>Open</button>
            <a href={d.pdf_url} target="_blank" rel="noreferrer" style={S.openLink} title="Open the raw PDF in a new tab">PDF</a>
            <button style={S.deleteBtn} title="Delete drawing" onClick={() => removeDrawing(d)} disabled={busyId === d.id}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="3" y1="3" x2="13" y2="13" strokeLinecap="round"/>
                <line x1="13" y1="3" x2="3" y2="13" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        ))}

      {editorDrawing && (
        <TakeoffEditor
          drawing={editorDrawing}
          onClose={() => setEditorDrawing(null)}
          onDrawingUpdated={row => {
            if (!row) return
            setDrawings(list => (list || []).map(x => x.id === row.id ? row : x))
            setEditorDrawing(prev => prev && prev.id === row.id ? row : prev)
          }}
        />
      )}

      {picker && (
        <CrmModal title="Add drawings" width={640}
          onClose={picker.phase === 'extracting' ? undefined : cancelPicker}
          footer={
            <>
              <button style={F.btnGhost} onClick={cancelPicker} disabled={picker.phase === 'extracting'}>Cancel</button>
              <button style={{ ...F.btnPrimary, opacity: selectedCount && picker.phase === 'pick' ? 1 : 0.5 }}
                onClick={confirmPicker} disabled={!selectedCount || picker.phase !== 'pick'}>
                {picker.phase === 'extracting' ? 'Adding…' : selectedCount ? `Add ${selectedCount} ${selectedCount === 1 ? 'drawing' : 'drawings'}` : 'Add drawings'}
              </button>
            </>
          }>
          <div style={S.pickerHint}>
            {picker.phase === 'loading'
              ? `Reading ${picker.fileName}…`
              : picker.phase === 'extracting'
                ? 'Extracting the selected pages…'
                : `Select the pages of ${picker.fileName} to keep as takeoff drawings. Each page becomes its own drawing.`}
          </div>
          <div style={S.pageGrid}>
            {picker.pages.map(pg => (
              <div key={pg.page}
                style={{ ...S.pageCard, borderColor: pg.selected ? 'var(--aj-accent)' : 'var(--aj-border)' }}>
                <button style={S.thumbBtn} onClick={() => picker.phase === 'pick' && setPage(pg.page, { selected: !pg.selected })}>
                  <img src={pg.thumb} alt={`Page ${pg.page}`} style={S.thumbImg} />
                  <span style={S.pageBadge}>
                    <input type="checkbox" checked={pg.selected} readOnly style={{ margin: 0 }} />
                    <span>p{pg.page}</span>
                  </span>
                </button>
                {pg.selected && (
                  <div style={S.pageFields}>
                    <input style={{ ...F.input, fontSize: 12, padding: '5px 7px' }} value={pg.name}
                      onChange={e => setPage(pg.page, { name: e.target.value })} placeholder="Drawing name" />
                    <select style={{ ...F.input, fontSize: 12, padding: '5px 7px' }} value={pg.drawingType}
                      onChange={e => setPage(pg.page, { drawingType: e.target.value })}>
                      <option value="">Type (optional)</option>
                      {DRAWING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                )}
              </div>
            ))}
            {picker.phase === 'loading' && <div style={S.muted}>Rendering pages…</div>}
          </div>
        </CrmModal>
      )}
    </>
  )
}

const S = {
  // Match the section styling in CrmOpportunityDetailModal
  sectionRow: { display: 'flex', alignItems: 'center', margin: '14px 0 6px', borderTop: '0.5px solid var(--aj-border)', paddingTop: 14 },
  sectionLbl: { fontFamily: '"Montserrat", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '4px 0' },
  row: { display: 'flex', alignItems: 'center', gap: 10, borderBottom: '0.5px solid var(--aj-border)', padding: '8px 0' },
  nameBtn: { display: 'block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)' },
  source: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  typeChip: { fontFamily: '"DM Mono", monospace', fontSize: 10, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-muted)', borderRadius: 4, padding: '2px 6px', flexShrink: 0 },
  scaledChip: { fontFamily: '"DM Mono", monospace', fontSize: 10, color: '#1a7f4e', background: 'rgba(26,127,78,0.12)', borderRadius: 4, padding: '2px 6px', flexShrink: 0 },
  unscaledChip: { fontFamily: '"DM Mono", monospace', fontSize: 10, color: 'var(--aj-text-muted)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '2px 6px', flexShrink: 0 },
  openBtn: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, fontWeight: 600, color: 'var(--aj-accent-blue)', flexShrink: 0 },
  openLink: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-accent-blue)', textDecoration: 'none', flexShrink: 0 },
  deleteBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', padding: 4, display: 'flex', alignItems: 'center', flexShrink: 0 },
  pickerHint: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', marginBottom: 12, lineHeight: 1.5 },
  pageGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 },
  pageCard: { border: '1px solid var(--aj-border)', borderRadius: 6, overflow: 'hidden', background: 'var(--aj-bg-subtle)' },
  thumbBtn: { display: 'block', width: '100%', position: 'relative', background: 'none', border: 'none', padding: 0, cursor: 'pointer' },
  thumbImg: { display: 'block', width: '100%', height: 'auto' },
  pageBadge: { position: 'absolute', top: 6, left: 6, display: 'flex', alignItems: 'center', gap: 5, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '3px 6px', fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: 'var(--aj-text)' },
  pageFields: { display: 'flex', flexDirection: 'column', gap: 6, padding: 8, borderTop: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-elevated)' },
}
